import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { GuestAccountJwtVO } from "@common/domain";
import {
  Actions,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import { RoomAccountDeleteEventVO } from "@youm-front/room/domain";
import { ROOM_APP_URL } from "@youm-front/room/provider";
import { firstValueFrom, Observable } from "rxjs";
import {
  catchError,
  concatMap,
  exhaustMap,
  mergeMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";
import { RoomAccountActions, RoomActions } from "../actions";
import { RoomAccountDeleteRequestDTO } from "../dto";
import { RoomAccountEvents } from "../events";
import {
  RoomAccountFacade,
  RoomContextFacade,
  RoomStreamFacade
} from "../facades";
import { RoomSelectors } from "../selectors";
import { RoomAccountService } from "../services";

@Injectable()
export class RoomAccountEffects implements OnRunEffects {
  readonly roomStreams$ = this.store.pipe(select(RoomSelectors.roomStreams));

  constructor(
    private readonly store: Store,
    private readonly accountJwtService: AccountJwtService,
    private readonly actions$: Actions,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly roomAccountFacade: RoomAccountFacade,
    private readonly roomAccountService: RoomAccountService,
    @Inject(ROOM_APP_URL) private readonly roomAppUrl: string,
    private readonly roomContextFacade: RoomContextFacade,
    private readonly roomStreamFacade: RoomStreamFacade
  ) {}

  listenRoomAccountDelete$ = createEffect(
    () =>
      this.roomAccountService.listenRoomAccountDelete().pipe(
        tap((_) =>
          this.roomAccountFacade.roomAccountDeleteEvent(
            new RoomAccountDeleteEventVO({
              accountId: _.data.accountId,
              roomContextId: _.data.roomContextId,
              roomId: _.data.roomId
            })
          )
        )
      ),
    { dispatch: false }
  );

  roomAccountDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomAccountActions.roomAccountDelete),
      concatMap((_) =>
        this.roomAccountService
          .roomAccountDelete(
            new RoomAccountDeleteRequestDTO({
              accountId: _.roomAccountDeleteCmd.value.accountId,
              roomContextId: _.roomAccountDeleteCmd.value.roomContextId,
              roomId: _.roomAccountDeleteCmd.value.roomId
            })
          )
          .pipe(
            mergeMap(() => [
              RoomAccountActions.roomAccountDeleteSuccess({
                roomAccountDeleteCmd: _.roomAccountDeleteCmd
              })
            ]),
            catchError((_) => [
              RoomAccountActions.roomAccountDeleteFailure({
                error: _.message
              })
            ])
          )
      )
    )
  );

  roomAccountDeleteEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RoomAccountEvents.roomAccountDeleteEvent),
        concatMap((_) =>
          this.roomAccountDeleteEventHandler(_.roomAccountDeleteEventVO)
        )
      ),
    {
      dispatch: false
    }
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }

  private async roomAccountDeleteEventHandler(
    roomAccountDeleteEventVO: RoomAccountDeleteEventVO
  ): Promise<void> {
    const { accountId, roomContextId, roomId } = roomAccountDeleteEventVO.value;

    const roomStreams = await firstValueFrom(this.roomStreams$);
    // Find 'Deleted Account' streams, of that 'Account' within that 'Room Context'
    const roomStreamsToDelete = roomStreams.filter(
      (_) =>
        _.value.roomId === roomId &&
        _.value.roomContextId === roomContextId &&
        _.value.accountId === accountId
    );

    // Delete 'Deleted Account' streams
    roomStreamsToDelete.forEach((_) =>
      this.roomStreamFacade.roomStreamDeleteEvent(_)
    );

    const accountJwtVO = await this.accountJwtService.anyAccountJwtVO$
      .pipe(take(1))
      .toPromise();

    // Particular 'Room Context' doesn't belong to the 'Deleted Account'
    if (
      roomContextId !== this.roomContextFacade.roomContextId ||
      accountId !== accountJwtVO?.value.id
    ) {
      return;
    }

    // Reset room to the initial state, should not keep any data
    this.store.dispatch(RoomActions.reset());
    // Particular 'Room Context' belongs to the 'Deleted Account'.
    // And it's a 'Guest Account', invalidate JWT and redirect to the '/invitation'
    if (accountJwtVO instanceof GuestAccountJwtVO) {
      return this.accountJwtService.accountJwtDelete("removed-from-room");
    }

    // Particular 'Room Context' belongs to the 'Deleted Account'.
    // And it's an 'Account', redirect the 'ROOM_APP_URL'
    this.document.location.href = this.roomAppUrl;
  }
}
